import { useCallback, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getUserData } from '@yme/api';
import { setAuthorizedStatusAction, setProfileDataAction, } from '@yme/components/Header/ProfileMenu/redux/actions';
import { getAuthorizedStatus, getProfileData, } from '@yme/components/Header/ProfileMenu/redux/selectors';
import { Network } from '@yme/services/network';
import { reloadPage } from '@yme/utils/common';
import { clearPreloadCache } from '../../utils/preloader';
import { AUTHENTICATION_LOGOUT_ENDPOINT, LOGIN_TIMEOUT_ENDPOINT, REFRESH_ACCESS_TOKEN_ENDPOINT, } from '../endpoints';
import { COOKIE_SESSION_EXPIRES_KEY, EXPIRED_SESSION_TIMESTAMP, SESSION_EXPIRY_MULTIPLY, TIME_RANGE, } from './constants';
const sessionExpiresIn = () => {
    const rawExpiredTime = Cookies.get(COOKIE_SESSION_EXPIRES_KEY);
    if (!rawExpiredTime) {
        return EXPIRED_SESSION_TIMESTAMP;
    }
    const expiredTime = Number(rawExpiredTime);
    if (!expiredTime || isNaN(expiredTime)) {
        return EXPIRED_SESSION_TIMESTAMP;
    }
    else {
        return expiredTime;
    }
};
const currentTimestamp = () => {
    const currentDate = new Date();
    const comparedValidTime = new Date(currentDate.getTime() + currentDate.getTimezoneOffset() * SESSION_EXPIRY_MULTIPLY).getTime();
    return comparedValidTime;
};
const shouldRefreshToken = () => {
    const expiredTime = sessionExpiresIn();
    const comparedValidTime = currentTimestamp();
    return expiredTime - comparedValidTime < TIME_RANGE * 2;
};
export const hasValidSession = () => {
    const expiredTime = sessionExpiresIn();
    const comparedValidTime = currentTimestamp();
    return expiredTime - comparedValidTime > 0;
};
// singleton
let isLogoutInProgress = false;
export function useAuthorization() {
    const timeoutRef = useRef();
    const [isExpiredSession, setIsExpiredSession] = useState(false);
    const dispatch = useDispatch();
    const isAuthorized = useSelector(getAuthorizedStatus);
    const userData = useSelector(getProfileData);
    const internalAuthorizedRef = useRef(isAuthorized);
    const userDataRef = useRef(userData);
    const location = useLocation();
    userDataRef.current = userData;
    const setAuthorized = useCallback(() => {
        if (internalAuthorizedRef.current) {
            return;
        }
        clearPreloadCache();
        internalAuthorizedRef.current = true;
        dispatch(setAuthorizedStatusAction(true));
    }, [dispatch]);
    const isCheckoutPage = useMemo(() => {
        return location.pathname.includes('checkout');
    }, [location.pathname]);
    const cleanAuthorizedState = useCallback(() => {
        Cookies.remove(COOKIE_SESSION_EXPIRES_KEY);
        if (!internalAuthorizedRef.current) {
            return;
        }
        internalAuthorizedRef.current = false;
        dispatch(setAuthorizedStatusAction(false));
        if (!isCheckoutPage) {
            reloadPage();
        }
    }, [dispatch, isCheckoutPage]);
    const fetchRefreshToken = useCallback(async () => {
        const emptyUserInfo = isEmpty(userDataRef.current?.customAttributes?.myYamahaUserId);
        let profileData = userDataRef.current ?? null;
        let isProfileLoadingErrored = false;
        try {
            if (!Cookies.get(COOKIE_SESSION_EXPIRES_KEY)) {
                throw new Error('Session expired');
            }
            else if (!emptyUserInfo) {
                throw new Error('User data is not empty');
            }
            profileData = await getUserData();
        }
        catch (error) {
            isProfileLoadingErrored = true;
        }
        if (!isProfileLoadingErrored) {
            if (emptyUserInfo) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                dispatch(setProfileDataAction(profileData));
                internalAuthorizedRef.current = true;
            }
            else {
                setAuthorized();
            }
            if (shouldRefreshToken()) {
                await Network.get(REFRESH_ACCESS_TOKEN_ENDPOINT);
            }
            return;
        }
        try {
            if (shouldRefreshToken() || emptyUserInfo) {
                const { status } = await Network.get(REFRESH_ACCESS_TOKEN_ENDPOINT);
                if (status !== 200) {
                    cleanAuthorizedState();
                    return;
                }
            }
            if (emptyUserInfo) {
                try {
                    const updatedProfileData = await getUserData();
                    dispatch(setProfileDataAction(updatedProfileData));
                    internalAuthorizedRef.current = true;
                }
                catch (error) {
                    cleanAuthorizedState();
                }
            }
            else {
                setAuthorized();
            }
        }
        catch (error) {
            cleanAuthorizedState();
        }
    }, [cleanAuthorizedState, dispatch, setAuthorized]);
    const setUnauthorized = async () => {
        if (isLogoutInProgress) {
            return;
        }
        clearTimeout(timeoutRef.current);
        isLogoutInProgress = true;
        try {
            await Network.get(AUTHENTICATION_LOGOUT_ENDPOINT);
        }
        catch (e) {
            // EOL;
        }
        finally {
            cleanAuthorizedState();
            Promise.resolve(() => {
                // setUnauthorized called multiple times during loging out
                // to prevent multiple calls to cleanAuthorizedState
                isLogoutInProgress = false;
            });
        }
    };
    const scheduleRefreshToken = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
        timeoutRef.current = setTimeout(async () => {
            try {
                if (hasValidSession()) {
                    await fetchRefreshToken();
                }
                else {
                    cleanAuthorizedState();
                }
            }
            catch (error) {
                // EOL;
            }
            finally {
                // re-schedule
                scheduleRefreshToken();
            }
        }, TIME_RANGE);
    }, [fetchRefreshToken, cleanAuthorizedState]);
    const detectAuthorizedStatus = useCallback(() => {
        const isSessionValid = hasValidSession();
        if (!timeoutRef.current) {
            scheduleRefreshToken();
        }
        if ((!isAuthorized && isSessionValid) || (!isSessionValid && isAuthorized)) {
            fetchRefreshToken();
        }
    }, [fetchRefreshToken, scheduleRefreshToken, isAuthorized]);
    const checkUserSession = useCallback(async () => {
        let isExpired = true;
        try {
            const response = await Network.get(LOGIN_TIMEOUT_ENDPOINT);
            isExpired = response?.status === 403;
            setIsExpiredSession(isExpired);
        }
        catch (err) {
            console.warn('Error checking user session:', err);
            setIsExpiredSession(true);
            return true;
        }
        return isExpired;
    }, []);
    return {
        setUnauthorized,
        detectAuthorizedStatus,
        checkUserSession,
        isExpiredSession,
        isAuthorized,
    };
}
